import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Tooltip,
  Card,
  Stack,
  List,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Autocomplete,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
} from "@mui/material";
import { readServiceTimeLog, readAsyncStorageValues, addTimeLogToServiceTicket } from "features/service/service.slice";
import moment from "moment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { toast } from "react-toastify";
import { translateText } from "utils/utilFunctions";
import CardSkeleton from "component/Loaders/CardSkeleton";
import CustomDebouncedButton from "component/ui/CustomDebouncedButton";

const Timelogs = ({ ticketId }) => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const { serviceTimeLogs, serviceTicketById, appConstants, isLoading } = useSelector((state) => state.service);
  const { user } = useSelector((state) => state.auth);

  const [showTimelogConfirmDialog, setShowTimelogConfirmDialog] = useState(false);
  const [showTimelogDialog, setShowTimelogDialog] = useState(false);
  const [startAndEndTime, setStartAndEndTime] = useState({ startTime: null, endTime: null });
  const [manualStartAndEndTime, setManualStartAndEndTime] = useState({ startTime: null, endTime: null });
  const [timelogNotes, setTimelogNotes] = useState("");
  const [isSubmittingTimelog, setIsSubmittingTimelog] = useState(false);

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(readServiceTimeLog(ticketId));
  }, [dispatch]);
  useEffect(() => {
    if (serviceTimeLogs.length && ticketId) {
      setManualStartAndEndTime({ ...manualStartAndEndTime, startTime: serviceTimeLogs.find((entry) => !entry.endTime)?.startTime || null });
    }
  }, [serviceTimeLogs]);
  const minutes_to_hhmm = (numberOfMinutes) => {
    //calculate hours
    var hh =
      numberOfMinutes.years() * (365 * 24) + numberOfMinutes.months() * (30 * 24) + numberOfMinutes.days() * 24 + numberOfMinutes.hours();
    //get minutes
    var mm = numberOfMinutes.minutes();
    //return total time in hh:mm format
    return hh + "h " + mm + " m";
  };
  const onPressSubmit = async () => {
    if (isSubmittingTimelog) return;
    if (!startAndEndTime.startTime) return toast.warn("Please enter start time!");
    if (!startAndEndTime.endTime) return toast.warn("Please enter end time!");
    if (moment(startAndEndTime.startTime).isAfter(moment(startAndEndTime.endTime)))
      return toast.warn("Start time should be earlier than end time!");
    if (!timelogNotes || !timelogNotes.trim()) return toast.warn("Please enter timelog notes!");

    const isTimelogOverlapped = serviceTimeLogs.filter(
      (entry) =>
        moment.utc(startAndEndTime.startTime).isBetween(moment(entry.startTime), moment(entry.endTime)) ||
        moment.utc(startAndEndTime.endTime).isBetween(moment(entry.startTime), moment(entry.endTime)) ||
        moment.utc(entry.startTime).isBetween(moment(startAndEndTime.startTime), moment(startAndEndTime.endTime)) ||
        moment.utc(entry.endTime).isBetween(moment(startAndEndTime.startTime), moment(startAndEndTime.endTime)) ||
        (moment.utc(entry.endTime).isSame(moment.utc(startAndEndTime.endTime)) &&
          moment.utc(entry.startTime).isSame(moment.utc(startAndEndTime.startTime)))
    );
    if (isTimelogOverlapped.length) return toast.warn("Timelogs cannot be overlapped.");
    if (startAndEndTime.startTime && startAndEndTime.endTime && timelogNotes && timelogNotes.trim().length) {
      if (moment(startAndEndTime.endTime).diff(moment(startAndEndTime.startTime), "seconds") > 60) {
        setIsSubmittingTimelog(true);
        try {
          const payload = { ...startAndEndTime };
          if (timelogNotes) {
            const translatedDescription = await translateText(timelogNotes);
            if (translatedDescription[0]?.detectedSourceLanguage !== "en")
              payload.timelogNotes = translatedDescription[0]?.translatedText || "N.A";
            else payload.timelogNotes = timelogNotes;
          }
          payload.serviceTicketId = ticketId;
          payload.type = appConstants.allTypes.TIME_LOG_TYPE.MANUAL;
          payload.startTime = moment.utc(payload.startTime).format("YYYY-MM-DDTHH:mm:ssZ");
          payload.endTime = moment.utc(payload.endTime).format("YYYY-MM-DDTHH:mm:ssZ");
          await dispatch(addTimeLogToServiceTicket(payload));
          await dispatch(readServiceTimeLog(ticketId));
          setShowTimelogDialog(false);
          setStartAndEndTime({ startTime: null, endTime: null });
          setTimelogNotes("");
          toast.success("Time log added.");
        } finally {
          setIsSubmittingTimelog(false);
        }
      } else return toast.warn("", "Timelog cannot be less than 1 minute.");
    } else return toast.warn("Please enter required fields!");
  };

  const onPunchInOrOut = async () => {
    if (isSubmittingTimelog) return;
    const punchoutPending = serviceTimeLogs.find((entry) => !entry.endTime);
    const punchinObj = {
      serviceTicketId: ticketId,
      type: appConstants.allTypes.TIME_LOG_TYPE.PUNCH_IN_PUNCH_OUT,
    };
    if (punchoutPending) {
      if (!manualStartAndEndTime.endTime) return toast.warn("Please enter end time!");
      if (moment(manualStartAndEndTime.endTime).diff(moment(manualStartAndEndTime.startTime), "seconds") <= 60)
        return toast.warn("Timelog cannot be less than 1 minute!");
      if (moment(manualStartAndEndTime.startTime).isAfter(moment(manualStartAndEndTime.endTime)))
        return toast.warn("End time should be after start time!");
      if (!timelogNotes || !timelogNotes.trim()) return toast.warn("Please enter timelog notes!");
      punchinObj.startTime = punchoutPending.startTime;
      punchinObj.timelogId = punchoutPending.id;
      punchinObj.endTime = moment.utc(manualStartAndEndTime.endTime).format("YYYY-MM-DDTHH:mm:ssZ");
    } else {
      punchinObj.startTime = moment.utc(new Date()).format("YYYY-MM-DDTHH:mm:ssZ");
    }

    setIsSubmittingTimelog(true);
    try {
      if (punchoutPending && timelogNotes) {
        const translatedDescription = await translateText(timelogNotes);
        if (translatedDescription[0]?.detectedSourceLanguage !== "en")
          punchinObj.timelogNotes = translatedDescription[0]?.translatedText || "";
        else punchinObj.timelogNotes = timelogNotes;
      }
      const res = await dispatch(addTimeLogToServiceTicket(punchinObj));
      if (res && res?.payload && res?.payload?.data) {
        toast.success(`Time log added.`);
        await dispatch(readServiceTimeLog(ticketId));
      }
      setManualStartAndEndTime({ startTime: null, endTime: null });
      setStartAndEndTime({ startTime: null, endTime: null });
      setTimelogNotes("");
      setShowTimelogConfirmDialog(false);
      setShowTimelogDialog(false);
    } finally {
      setIsSubmittingTimelog(false);
    }
  };
  const confirmMessageContent = () => {
    const type = serviceTimeLogs.find((entry) => !entry.endTime) ? `Punch Out` : `Punch In`;
    return (
      <div>
        <Typography fontWeight={"bold"}>{`${type}!`}</Typography>
        <Typography>{`Are you sure that you want to ${type} ?`}</Typography>
      </div>
    );
  };

  if (isLoading)
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 50,
          paddingBottom: 50,
        }}
      >
        <CardSkeleton count={3} />
      </div>
    );
  return (
    <div>
      <div>
        <Dialog open={showTimelogConfirmDialog} sx={{ marginTop: -2 }}>
          <DialogContent sx={{ mb: 1 }}>{confirmMessageContent()}</DialogContent>
          <Stack direction="row" justifyContent={"flex-end"} mb={2} mx={2} columnGap={2}>
            <Button
              variant="contained"
              disabled={isSubmittingTimelog}
              onClick={() => {
                setManualStartAndEndTime({
                  endTime: null,
                  startTime: serviceTimeLogs.find((entry) => !entry.endTime)?.startTime || null,
                });
                setStartAndEndTime({ startTime: null, endTime: null });
                setShowTimelogConfirmDialog(false);
              }}
            >
              Cancel
            </Button>
            <CustomDebouncedButton
              buttonText="Submit"
              onPress={onPunchInOrOut}
              loading={isSubmittingTimelog}
              variant="contained"
              sx={{ mx: 0, mt: 0, mb: 0 }}
            />
          </Stack>
        </Dialog>
      </div>
      <div>
        <Dialog open={showTimelogDialog} sx={{ marginTop: -25 }}>
          <DialogTitle fontWeight={"bold"}>Add timelog</DialogTitle>
          <DialogContent sx={{ mb: 3 }}>
            {serviceTimeLogs.find((entry) => !entry.endTime) ? (
              <Stack direction={"row"} my={1} columnGap={3} alignItems={"center"}>
                <Button
                  onClick={() => {
                    serviceTimeLogs.find((entry) => !entry.endTime)
                      ? setManualStartAndEndTime({
                          ...manualStartAndEndTime,
                          endTime: moment.utc(new Date()).format("YYYY-MM-DDTHH:mm:ssZ"),
                        })
                      : setShowTimelogConfirmDialog(true);
                  }}
                >
                  {serviceTimeLogs.find((entry) => !entry.endTime) ? "Punch out" : "Punch in"}
                </Button>
                <Typography>Or</Typography>
                {serviceTimeLogs.find((entry) => !entry.endTime) ? (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      slotProps={{ textField: { size: "small" } }}
                      minDate={moment(moment(serviceTimeLogs.find((entry) => !entry.endTime)?.startTime || new Date()).toDate())}
                      maxDate={moment(new Date())}
                      label="End time"
                      format={"DD MMM YYYY HH:mm"}
                      value={manualStartAndEndTime.endTime ? moment(manualStartAndEndTime.endTime) : null}
                      closeOnSelect={false}
                      variant="standard"
                      sx={{
                        minWidth: 220,
                        maxWidth: 600,
                        "& .Mui-error": {
                          color: "black",
                        },
                        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#C8C8C8",
                          borderBlockColor: "#C8C8C8",
                          color: "black",
                        },
                        "& .MuiInputLabel-root": {
                          color: "#888888",
                        },
                      }}
                      onChange={(newValue) => setManualStartAndEndTime({ ...manualStartAndEndTime, endTime: newValue })}
                    />
                  </LocalizationProvider>
                ) : null}
              </Stack>
            ) : (
              <Stack>
                <Button
                  onClick={() => {
                    serviceTimeLogs.find((entry) => !entry.endTime)
                      ? setManualStartAndEndTime({
                          ...manualStartAndEndTime,
                          endTime: moment.utc(new Date()).format("YYYY-MM-DDTHH:mm:ssZ"),
                        })
                      : setShowTimelogConfirmDialog(true);
                  }}
                >
                  {serviceTimeLogs.find((entry) => !entry.endTime) ? "Punch out" : "Punch in"}
                </Button>
                <Stack alignItems={"center"} my={1}>
                  <Typography>Or</Typography>
                </Stack>
              </Stack>
            )}
            {manualStartAndEndTime.endTime ? (
              <Stack alignItems={"center"} my={2}>
                <Typography>Start time : {moment(manualStartAndEndTime.startTime).format("DD MMM YY HH:mm")}</Typography>
                <Typography>
                  You are logging{" "}
                  {manualStartAndEndTime.endTime
                    ? minutes_to_hhmm(
                        moment.duration(
                          moment(moment(manualStartAndEndTime.endTime).format("DD MMM YY HH:mm"), "DD MMM YY hh:mm").diff(
                            moment(moment(manualStartAndEndTime.startTime).format("DD MMM YY HH:mm"), "DD MMM YY hh:mm"),
                            "minutes"
                          ),
                          "minutes"
                        )
                      )
                    : "-"}{" "}
                </Typography>
              </Stack>
            ) : null}
            {serviceTimeLogs.find((entry) => !entry.endTime) ? null : (
              <Stack direction={"row"} my={1} columnGap={2}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    slotProps={{ textField: { size: "small" } }}
                    minDate={moment(moment(serviceTicketById.dateCreated).toDate())}
                    maxDate={moment(new Date())}
                    label="Start time"
                    format={"DD MMM YYYY HH:mm"}
                    value={startAndEndTime.startTime ? moment(startAndEndTime.startTime) : null}
                    // onChange={calculateDuedate}
                    closeOnSelect={false}
                    variant="standard"
                    sx={{
                      minWidth: 250,
                      maxWidth: 600,
                      "& .Mui-error": {
                        color: "black",
                      },
                      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#C8C8C8",
                        borderBlockColor: "#C8C8C8",
                        color: "black",
                      },
                      "& .MuiInputLabel-root": {
                        color: "#888888",
                      },
                    }}
                    onChange={(newValue) => setStartAndEndTime({ ...startAndEndTime, startTime: newValue, endTime: null })}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    slotProps={{ textField: { size: "small" } }}
                    minDateTime={moment(startAndEndTime.startTime).add("minute", 2)}
                    minDate={moment(startAndEndTime.startTime)}
                    maxDate={moment(startAndEndTime.startTime)}
                    label="End time"
                    format={"DD MMM YYYY HH:mm"}
                    disabled={startAndEndTime.startTime ? false : true}
                    value={startAndEndTime.endTime ? moment(startAndEndTime.endTime) : null}
                    // onChange={calculateDuedate}
                    closeOnSelect={false}
                    variant="standard"
                    sx={{
                      minWidth: 250,
                      maxWidth: 600,
                      "& .Mui-error": {
                        color: "black",
                      },
                      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#C8C8C8",
                        borderBlockColor: "#C8C8C8",
                        color: "black",
                      },
                      "& .MuiInputLabel-root": {
                        color: "#888888",
                      },
                    }}
                    onChange={(newValue, e) => setStartAndEndTime({ ...startAndEndTime, endTime: newValue })}
                  />
                </LocalizationProvider>
              </Stack>
            )}
            <TextField
              sx={{ mt: 1 }}
              label="Timelog notes"
              fullWidth
              multiline
              maxRows={8}
              onChange={(e) => setTimelogNotes(e.target.value)}
            />
          </DialogContent>
          <Stack direction="row" justifyContent={"flex-end"} mb={2} mx={2} columnGap={2}>
            <Button
              variant="contained"
              disabled={isSubmittingTimelog}
              onClick={() => {
                setManualStartAndEndTime({
                  endTime: null,
                  startTime: serviceTimeLogs.find((entry) => !entry.endTime)?.startTime || null,
                });
                setStartAndEndTime({ startTime: null, endTime: null });
                setShowTimelogDialog(false);
              }}
            >
              Cancel
            </Button>
            <CustomDebouncedButton
              buttonText="Submit"
              onPress={() => (serviceTimeLogs.find((entry) => !entry.endTime) ? onPunchInOrOut() : onPressSubmit())}
              loading={isSubmittingTimelog}
              variant="contained"
              sx={{ mx: 0, mt: 0, mb: 0 }}
            />
          </Stack>
        </Dialog>
      </div>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Timelogs
        </Typography>
        {serviceTicketById.assignees?.length &&
        serviceTicketById.assignees
          ?.map((item) => item.id)
          .includes(appConstants?.appUsers?.find((users) => users.userName == user.username)?.userId) ? (
          <Tooltip title="Add timelog">
            <Button sx={{ height: "self" }} variant="contained" onClick={() => setShowTimelogDialog(true)}>
              Add
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
      <Stack mb={1}>
        <List
          sx={{
            overflow: "auto",
            maxHeight: 500,
            p: 0,
          }}
        >
          {serviceTimeLogs.length ? (
            serviceTimeLogs.map((timeLog, idx) => (
              <Paper
                elevation={3}
                key={timeLog.id || idx}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  background: theme.palette.mode === "dark" ? "#23272f" : "#f9f9fb",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                  p: 2,
                  minWidth: 220,
                  borderLeft: `6px solid ${theme.palette.primary.main}`,
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
                  },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} columnGap={2}>
                  <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                      color="primary"
                      fontWeight={700}
                      sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {appConstants && Object.keys(appConstants).length
                        ? (() => {
                            const engineer =
                              appConstants.allAppUsers.find((user) => user.userName === timeLog.serviceEngineer) || {};
                            const name = (engineer.name || "-").slice(0, 12);
                            const username = engineer.userName || timeLog.serviceEngineer || "";
                            return `${name} (${username})`;
                          })()
                        : `[${timeLog.serviceEngineer || ""}]`}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" flexShrink={0}>
                    <Typography>Hours:</Typography>
                    <Typography color="secondary" fontWeight={600} ml={2}>
                      {timeLog.endTime
                        ? minutes_to_hhmm(
                            moment.duration(
                              moment(moment(timeLog.endTime).format("DD MMM YY HH:mm"), "DD MMM YY hh:mm").diff(
                                moment(moment(timeLog.startTime).format("DD MMM YY HH:mm"), "DD MMM YY hh:mm"),
                                "minutes"
                              ),
                              "minutes"
                            )
                          )
                        : " - "}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  columnGap={6}
                  rowGap={0.5}
                  mb={0.5}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Start time:</Typography>
                    <Typography>{timeLog?.startTime ? moment(timeLog.startTime).format("DD MMM YY HH:mm") : "N.A"}</Typography>
                  </Stack>
                  {/* <Typography sx={{ opacity: 0.6 }}>|</Typography> */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>End time:</Typography>
                    <Typography>{timeLog?.endTime ? moment(timeLog.endTime).format("DD MMM YY HH:mm") : "N.A"}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            ))
          ) : (
            <Paper elevation={0} sx={{ p: 3, textAlign: "center", color: theme.palette.text.disabled, background: "#f5f5f7" }}>
              No timelogs.
            </Paper>
          )}
        </List>
      </Stack>
    </div>
  );
};

export default Timelogs;
